import React, { useState, useRef, useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { Icon } from "components/icons/icon";
import { useNavigate } from "react-router-dom";
import { Button } from "components/ui/Button";
import { selectIsPricingBusy, selectMappedShoppingCartItems, selectShoppingCartItemsMemoized } from "../../slices/pricing/selectors";
import { removeShoppingCartItem } from "../../slices/pricing/pricingSlice";
import { createQuote } from "../../slices/pricing/thunks/createQuoteThunk";
import { useSubscriptionProcessState, useRiskData } from "features/subscription-process/contexts/selectors/selectors";
import { GetRgiRiskResponse, GetUnemploymentRiskResponse, GetUpiRiskResponse } from "api/risk";

export const ShoppingCart: React.FC<{ riskId?: string }> = ({ riskId }) => {
  const dispatch = useAppDispatch();
  const { branchCode } = useSubscriptionProcessState()
  const riskData = useRiskData() as GetUnemploymentRiskResponse | GetUpiRiskResponse | GetRgiRiskResponse | null
  const person = riskData?.person

  const items = useAppSelector(selectMappedShoppingCartItems)

  const mappedShoppingCartItems = useMemo(() => {
    if (!person || !riskId) {
      return []
    }
    return items.map((command) => ({
      ...command,
      riskId,
      personId: person.id,
      branchCode,
    }))
  }, [person, riskId, items, branchCode])

  const navigate = useNavigate();
  const shoppingCartItems = useAppSelector(selectShoppingCartItemsMemoized);
  // Re-tarif global en cours : on bloque la création de devis (prix périmés).
  const isPricingBusy = useAppSelector(selectIsPricingBusy);
  const [isOpen, setIsOpen] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);

  const toggleModal = () => {
    setIsOpen((prev) => !prev);
  };

  const removeItem = (hash: string) => {
    dispatch(removeShoppingCartItem(hash))
  };

  const handleOverlayClick = (e: any) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false);
    }
  };

  const createDevis = useCallback(async () => {
    if (mappedShoppingCartItems && branchCode && riskId) {
      await dispatch(createQuote({
        mappedShoppingCartItems,
        navigate,
        setIsOpen,
        branchCode,
        riskId
      }));
    }
  }, [mappedShoppingCartItems, navigate, dispatch, branchCode, riskId]);

  const displayNormalSelectedItems = () => {
    return (
      <>
        <p className={`mt-2.5 text-primary font-bold text-base ${shoppingCartItems.length > 0 && 'border-b border-gray-300'} pb-4`}>
          Vous avez sélectionné {shoppingCartItems.length} offre{shoppingCartItems.length > 1 ? "s" : ""}
        </p>
        <ul className="my-4 flex flex-col gap-4">
          {shoppingCartItems.map((item, index: number) => item && (
            <div className="flex justify-between items-center"
              key={"Shopping" + index}
            >
              <div>
                <li key={index}>
                  <div className="flex flex-col">
                    <p className="text-xs text-primary uppercase">
                      Contrat {item.supportLabel}
                    </p>
                    <p className="font-bold text-xs">
                      {
                        `${item.name} (${item.productOwner})`
                      }
                    </p>
                    <p className="text-gray-500 text-xs">
                      {item.contractIdentifier}
                    </p>
                    <p className="text-sm font-bold mt-2">
                      {item.clientPrice} €
                    </p>
                  </div>
                </li>
              </div>
              <div
                className="cursor-pointer"
                onClick={() => removeItem(item.hash || '')}
              >
                <Icon name="trash-circle" styles="" width="35" height="35" />
              </div>
            </div>
          ))}
        </ul>
      </>
    )
  }

  return (
    <div className="fixed bottom-5 right-5 z-20">
      <div className="relative z-30 transition-all duration-300" onClick={toggleModal}>
        <div className="bg-primary text-white p-5 border-none rounded-full cursor-pointer shadow-lg relative">
          <Icon name="shopping" styles="" />
          {shoppingCartItems.length > 0 && (
            <div className="absolute -top-1 -right-0.5 bg-secondary text-white rounded-full px-2 py-1 text-sm font-bold min-w-[28px] h-7 flex items-center justify-center">
              <p className="text-sm font-bold">{shoppingCartItems.length}</p>
            </div>
          )}
        </div>
      </div>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-35 flex items-center justify-center z-20"
          onClick={handleOverlayClick}
        >
          <div className="absolute bottom-[2.5vw] right-[2.5vw] overflow-hidden bg-white shadow-lg rounded-2xl" ref={modalRef}>
            <div className="bg-white shadow-lg p-6 relative min-w-[400px]">
              <h2 className="text-xl font-semibold mb-4">Votre panier</h2>
              {displayNormalSelectedItems()}
              <div className="flex mt-2.5 gap-2">
                <Button color="secondary" onClick={() => createDevis()} disabled={isPricingBusy}>
                  {shoppingCartItems.length > 1
                    ? "Créer les simulations"
                    : "Créer la simulation"}
                </Button>
                <Button outline
                  onClick={() => setIsOpen(false)}>
                  Retour au tarificateur
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
